import {
  HHBox,
  HHButton,
  HHCard,
  HHCardContent,
  HHCircularProgress,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import {
  ArrowBack,
  CloudOff,
  Refresh,
  Rule,
  Storage,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useMemo, useState } from 'react';
import { RuleStatus, useListRulesQuery } from '../../hinge-connect/rules/types';
import { isFileIngestionRule } from '../constants/file-ingestion.constant';
import { useStatusToggle } from '../hooks/use-status-toggle';
import { StatusChangeConfirmationDialog } from './status-change-confirmation-dialog';
import { StatusDropdownMenu } from './status-dropdown-menu';

type ProviderConfigsViewProps = {
  providerSource: string;
  onBack: () => void;
  onRuleClick: (ruleId: string) => void;
};

export const ProviderConfigsView = ({
  providerSource,
  onBack,
  onRuleClick,
}: ProviderConfigsViewProps): JSX.Element => {
  const {
    data: rulesData,
    loading,
    error,
  } = useListRulesQuery({
    variables: {
      listRulesInput: {
        source: providerSource,
      },
    },
  });

  // Status change functionality
  const [confirmationDialog, setConfirmationDialog] = useState<{
    open: boolean;
    ruleId: string;
    ruleName: string;
    currentStatus: RuleStatus;
    targetStatus: RuleStatus;
  }>({
    open: false,
    ruleId: '',
    ruleName: '',
    currentStatus: RuleStatus.Draft,
    targetStatus: RuleStatus.Published,
  });

  const { loading: statusUpdateLoading, updateRuleStatus } = useStatusToggle();

  const getStatusColor = (status: RuleStatus): string => {
    switch (status) {
      case RuleStatus.Published:
        return '#2E7D32'; // Green
      case RuleStatus.Unpublished:
        return '#FF9800'; // Orange
      case RuleStatus.Draft:
        return '#757575'; // Gray
      default:
        return '#757575'; // Gray fallback
    }
  };

  const formatDate = (dateString: string): string => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy • h:mm a');
    } catch {
      return 'Unknown';
    }
  };

  // Status change handlers
  const handleStatusChange = (
    ruleId: string,
    ruleName: string,
    currentStatus: RuleStatus,
    targetStatus: RuleStatus,
  ): void => {
    setConfirmationDialog({
      open: true,
      ruleId,
      ruleName,
      currentStatus,
      targetStatus,
    });
  };

  const handleConfirmStatusChange = async (): Promise<void> => {
    try {
      await updateRuleStatus(
        confirmationDialog.ruleId,
        confirmationDialog.targetStatus,
      );
      setConfirmationDialog(prev => ({ ...prev, open: false }));
    } catch (error) {
      console.error('Failed to update rule status:', error);
    }
  };

  const handleCancelStatusChange = (): void => {
    setConfirmationDialog(prev => ({ ...prev, open: false }));
  };

  const rules = useMemo(() => {
    const allRules = rulesData?.listRules?.rules || [];
    return allRules.filter(rule =>
      isFileIngestionRule(rule.resourceTypes || []),
    );
  }, [rulesData]);

  if (loading) {
    return (
      <HHBox paddingX="0" bgcolor="#ffffff">
        <HHStack direction="column" spacing={8} height="100%">
          {/* Back Navigation */}
          <HHBox paddingX="2rem" paddingTop="1rem">
            <HHButton
              hhVariant="text"
              startIcon={<ArrowBack />}
              onClick={onBack}
              sx={{
                color: '#1565C0',
                fontWeight: 500,
                textTransform: 'none',
                padding: '6px 12px',
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: '#E3F2FD',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              Back to Providers
            </HHButton>
          </HHBox>

          {/* Header */}
          <HHBox
            sx={{
              position: 'relative',
              marginBottom: '16px',
              paddingX: '2rem',
              paddingTop: '1rem',
            }}
          >
            <HHStack direction="row" alignItems="center" spacing={3}>
              <HHBox
                sx={{
                  backgroundColor: '#1565C0',
                  background:
                    'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
                  borderRadius: '16px',
                  padding: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 20px rgba(21, 101, 192, 0.25)',
                }}
              >
                <Rule
                  sx={{
                    fontSize: '32px',
                    color: 'white',
                  }}
                />
              </HHBox>
              <HHStack direction="column" spacing={0.5} sx={{ flex: 1 }}>
                <HHTypography
                  hhVariant="h1"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '24px', sm: '28px', md: '32px' },
                    lineHeight: 1.2,
                    background:
                      'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    color: 'transparent',
                    letterSpacing: '-0.02em',
                  }}
                >
                  {providerSource} File Ingestion Configurations
                </HHTypography>
                <HHTypography
                  hhVariant="body1"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '16px',
                    fontWeight: 400,
                    marginTop: '4px',
                  }}
                >
                  Loading configurations...
                </HHTypography>
              </HHStack>
            </HHStack>

            {/* Decorative accent line */}
            <HHBox
              sx={{
                position: 'absolute',
                bottom: '-12px',
                left: '34px',
                width: '100px',
                height: '4px',
                background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
                borderRadius: '2px',
                opacity: 0.6,
              }}
            />
          </HHBox>

          {/* Loading Section */}
          <HHBox
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              paddingY: 8,
              paddingX: '2rem',
              textAlign: 'center',
              minHeight: '400px',
            }}
          >
            <HHCircularProgress
              size={60}
              thickness={3}
              sx={{
                color: '#1565C0',
                marginBottom: 3,
              }}
            />
            <HHTypography
              hhVariant="h5"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                marginBottom: 1,
              }}
            >
              Loading Rules
            </HHTypography>
            <HHTypography
              hhVariant="body1"
              sx={{
                color: 'text.secondary',
                maxWidth: '400px',
                lineHeight: 1.6,
              }}
            >
              Fetching configurations for {providerSource}...
            </HHTypography>
          </HHBox>
        </HHStack>
      </HHBox>
    );
  }

  if (error) {
    return (
      <HHBox paddingX="0" bgcolor="#ffffff">
        <HHStack direction="column" spacing={6} height="100%">
          {/* Back Navigation */}
          <HHBox paddingX="2rem" paddingTop="1rem">
            <HHButton
              hhVariant="text"
              startIcon={<ArrowBack />}
              onClick={onBack}
              sx={{
                color: '#1565C0',
                fontWeight: 500,
                textTransform: 'none',
                padding: '6px 12px',
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: '#E3F2FD',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              Back to Providers
            </HHButton>
          </HHBox>

          {/* Error Content */}
          <HHBox
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              paddingY: 8,
              paddingX: '2rem',
              textAlign: 'center',
              minHeight: '500px',
            }}
          >
            {/* Error Icon */}
            <HHBox
              sx={{
                backgroundColor: '#FFEBEE',
                background: 'linear-gradient(135deg, #FFEBEE 0%, #FFCDD2 100%)',
                borderRadius: '50%',
                padding: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 3,
                boxShadow: '0 8px 32px rgba(244, 67, 54, 0.15)',
                border: '2px solid #FFCDD2',
              }}
            >
              <CloudOff
                sx={{
                  fontSize: '64px',
                  color: '#D32F2F',
                }}
              />
            </HHBox>

            {/* Error Title */}
            <HHTypography
              hhVariant="h1"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '28px', sm: '32px', md: '36px' },
                lineHeight: 1.2,
                color: '#D32F2F',
                marginBottom: 2,
                letterSpacing: '-0.02em',
              }}
            >
              Configurations Unavailable
            </HHTypography>

            {/* Error Description */}
            <HHTypography
              hhVariant="body1"
              sx={{
                color: 'text.secondary',
                fontSize: '18px',
                lineHeight: 1.6,
                maxWidth: '500px',
                marginBottom: 4,
              }}
            >
              We couldn't load the configurations for {providerSource}. This
              might be due to a network issue or the provider data may no longer
              be accessible.
            </HHTypography>

            {/* Error Details */}
            <HHBox
              sx={{
                backgroundColor: '#F5F5F5',
                border: '1px solid #E0E0E0',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: 4,
                maxWidth: '500px',
                width: '100%',
              }}
            >
              <HHStack direction="column" spacing={2}>
                <HHTypography
                  hhVariant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: '16px',
                    color: 'text.primary',
                  }}
                >
                  What you can try:
                </HHTypography>
                <HHStack direction="column" spacing={1} alignItems="flex-start">
                  <HHStack direction="row" alignItems="center" spacing={2}>
                    <HHBox
                      sx={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: '#D32F2F',
                      }}
                    />
                    <HHTypography
                      hhVariant="body2"
                      sx={{ color: 'text.secondary', fontSize: '14px' }}
                    >
                      Check your internet connection
                    </HHTypography>
                  </HHStack>
                  <HHStack direction="row" alignItems="center" spacing={2}>
                    <HHBox
                      sx={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: '#D32F2F',
                      }}
                    />
                    <HHTypography
                      hhVariant="body2"
                      sx={{ color: 'text.secondary', fontSize: '14px' }}
                    >
                      Refresh the page and try again
                    </HHTypography>
                  </HHStack>
                  <HHStack direction="row" alignItems="center" spacing={2}>
                    <HHBox
                      sx={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: '#D32F2F',
                      }}
                    />
                    <HHTypography
                      hhVariant="body2"
                      sx={{ color: 'text.secondary', fontSize: '14px' }}
                    >
                      Return to providers and try again
                    </HHTypography>
                  </HHStack>
                </HHStack>
              </HHStack>
            </HHBox>

            {/* Action Buttons */}
            <HHStack
              direction="row"
              spacing={2}
              flexWrap="wrap"
              justifyContent="center"
            >
              <HHButton
                hhVariant="contained"
                startIcon={<Refresh />}
                onClick={(): void => window.location.reload()}
                sx={{
                  backgroundColor: '#D32F2F',
                  color: 'white',
                  fontWeight: 600,
                  textTransform: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: '#B71C1C',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                Try Again
              </HHButton>
              <HHButton
                hhVariant="outlined"
                onClick={onBack}
                sx={{
                  borderColor: '#D32F2F',
                  color: '#D32F2F',
                  fontWeight: 600,
                  textTransform: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: '#FFEBEE',
                    borderColor: '#B71C1C',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                Go Back
              </HHButton>
            </HHStack>

            {/* Technical Details (if available) */}
            {error && (
              <HHBox
                sx={{
                  marginTop: 4,
                  padding: '16px',
                  backgroundColor: '#FFF3E0',
                  border: '1px solid #FFB74D',
                  borderRadius: '8px',
                  maxWidth: '500px',
                  width: '100%',
                }}
              >
                <HHTypography
                  hhVariant="caption"
                  sx={{
                    color: '#E65100',
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    wordBreak: 'break-word',
                  }}
                >
                  Error: {error.message || 'Unknown error occurred'}
                </HHTypography>
              </HHBox>
            )}
          </HHBox>
        </HHStack>
      </HHBox>
    );
  }

  return (
    <HHBox paddingX="0" bgcolor="#ffffff">
      <HHStack direction="column" spacing={6} height="100%">
        {/* Back Navigation */}
        <HHBox paddingX="2rem" paddingTop="1rem">
          <HHButton
            hhVariant="text"
            startIcon={<ArrowBack />}
            onClick={onBack}
            sx={{
              color: '#1565C0',
              fontWeight: 500,
              textTransform: 'none',
              padding: '6px 12px',
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: '#E3F2FD',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            Back to Providers
          </HHButton>
        </HHBox>

        {/* Header */}
        <HHBox
          sx={{
            position: 'relative',
            marginBottom: '16px',
            paddingX: '2rem',
            paddingTop: '1rem',
          }}
        >
          <HHStack direction="row" alignItems="center" spacing={3}>
            <HHBox
              sx={{
                backgroundColor: '#1565C0',
                background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
                borderRadius: '16px',
                padding: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 20px rgba(21, 101, 192, 0.25)',
              }}
            >
              <Rule
                sx={{
                  fontSize: '32px',
                  color: 'white',
                }}
              />
            </HHBox>
            <HHStack direction="column" spacing={0.5} sx={{ flex: 1 }}>
              <HHTypography
                hhVariant="h1"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '24px', sm: '28px', md: '32px' },
                  lineHeight: 1.2,
                  background:
                    'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  color: 'transparent',
                  letterSpacing: '-0.02em',
                }}
              >
                {providerSource} File Ingestion Configurations
              </HHTypography>
              <HHTypography
                hhVariant="body1"
                sx={{
                  color: 'text.secondary',
                  fontSize: '16px',
                  fontWeight: 400,
                  marginTop: '4px',
                }}
              >
                {rules.length} file ingestion configuration
                {rules.length !== 1 ? 's' : ''} found
              </HHTypography>
            </HHStack>
          </HHStack>

          {/* Decorative accent line */}
          <HHBox
            sx={{
              position: 'absolute',
              bottom: '-12px',
              left: '34px',
              width: '100px',
              height: '4px',
              background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
              borderRadius: '2px',
              opacity: 0.6,
            }}
          />
        </HHBox>

        {/* Rules List */}
        <HHBox paddingX="2rem">
          {rules.length === 0 ? (
            <HHBox
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                paddingY: 8,
                textAlign: 'center',
                minHeight: '300px',
              }}
            >
              <Storage
                sx={{
                  fontSize: '64px',
                  color: 'text.disabled',
                  marginBottom: 2,
                }}
              />
              <HHTypography
                hhVariant="h5"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  marginBottom: 1,
                }}
              >
                No File Ingestion Configurations Found
              </HHTypography>
              <HHTypography
                hhVariant="body1"
                sx={{
                  color: 'text.secondary',
                  maxWidth: '400px',
                  lineHeight: 1.6,
                }}
              >
                No file ingestion configurations are currently configured for{' '}
                {providerSource}
              </HHTypography>
            </HHBox>
          ) : (
            <HHStack direction="column" spacing={3}>
              {rules.map(rule => {
                const statusColor = getStatusColor(rule.status);
                const isUpdatingThisRule =
                  statusUpdateLoading && confirmationDialog.ruleId === rule.id;

                return (
                  <HHCard
                    key={rule.id}
                    onClick={
                      !isUpdatingThisRule
                        ? (): void => onRuleClick(rule.id)
                        : undefined
                    }
                    sx={{
                      border: '1px solid #E0E0E0',
                      borderRadius: '12px',
                      backgroundColor: `${statusColor}03`,
                      cursor: isUpdatingThisRule ? 'default' : 'pointer',
                      opacity: isUpdatingThisRule ? 0.7 : 1,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': !isUpdatingThisRule
                        ? {
                            borderColor: statusColor,
                            backgroundColor: `${statusColor}08`,
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                            transform: 'translateY(-2px)',
                          }
                        : {},
                    }}
                  >
                    <HHCardContent sx={{ padding: '24px' }}>
                      <HHStack direction="column" spacing={2}>
                        <HHStack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="flex-start"
                        >
                          <HHStack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                          >
                            <HHStack direction="column" spacing={0.5}>
                              <HHTypography
                                hhVariant="h6"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: '18px',
                                  color: 'text.primary',
                                }}
                              >
                                {rule.definition.name}
                              </HHTypography>
                              <HHStack
                                direction="row"
                                spacing={2}
                                alignItems="center"
                              >
                                <HHTypography
                                  hhVariant="body2"
                                  sx={{
                                    color: 'text.secondary',
                                    fontSize: '14px',
                                  }}
                                >
                                  Config ID: {rule.id}
                                </HHTypography>
                                <HHTypography
                                  hhVariant="body2"
                                  sx={{
                                    color: 'text.secondary',
                                    fontSize: '14px',
                                  }}
                                >
                                  • Updated: {formatDate(rule.updatedAt)}
                                </HHTypography>
                              </HHStack>
                            </HHStack>
                          </HHStack>

                          <StatusDropdownMenu
                            currentStatus={rule.status}
                            loading={isUpdatingThisRule}
                            onStatusChange={(newStatus): void =>
                              handleStatusChange(
                                rule.id,
                                rule.definition.name,
                                rule.status,
                                newStatus,
                              )
                            }
                          />
                        </HHStack>
                      </HHStack>
                    </HHCardContent>
                  </HHCard>
                );
              })}
            </HHStack>
          )}
        </HHBox>
      </HHStack>

      {/* Status Change Confirmation Dialog */}
      <StatusChangeConfirmationDialog
        open={confirmationDialog.open}
        currentStatus={confirmationDialog.currentStatus}
        targetStatus={confirmationDialog.targetStatus}
        ruleName={confirmationDialog.ruleName}
        loading={statusUpdateLoading}
        onConfirm={handleConfirmStatusChange}
        onCancel={handleCancelStatusChange}
      />
    </HHBox>
  );
};
